import { OfficeAdapter } from './office-adapter';
import type { AppointmentSnapshot, OfficeAdapterEvents, OfficeThemeMode, OutlookContext, PaneInitializationContext } from './office-types';
import type { SimHostState } from './sim-host-state';

type Handlers = { [E in keyof OfficeAdapterEvents]: Set<OfficeAdapterEvents[E]> };

/**
 * Dev-harness adapter: implements the identical Office.js contract over
 * SimHostState so the pane ↔ appointment behaviours (attach/detach, retime,
 * AppointmentTimeChanged, EnhancedLocationsChanged) are exercised end to end
 * without an M365 tenant.
 */
export class SimOfficeAdapter extends OfficeAdapter {
  private readonly handlers: Handlers = {
    timeChanged: new Set(),
    locationsChanged: new Set(),
    recipientsChanged: new Set(),
    themeChanged: new Set(),
  };

  constructor(private readonly host: SimHostState) {
    super();
  }

  override context(): OutlookContext {
    return this.host.context();
  }

  override canWriteItem(): boolean {
    return this.host.context() === 'compose';
  }

  override userEmail(): string | null {
    return this.host.userEmail();
  }

  override readAppointment(): Promise<AppointmentSnapshot | null> {
    if (this.host.context() === 'mail') return Promise.resolve(null);
    return Promise.resolve(this.snapshot());
  }

  override setAppointmentTimes(start: Date, end: Date): Promise<void> {
    this.host.logCall(`item.start.setAsync(${start.toLocaleTimeString()}) · item.end.setAsync(${end.toLocaleTimeString()})`);
    this.host.start.set(start);
    this.host.end.set(end);
    return Promise.resolve();
  }

  override addRoomLocation(roomEmail: string, displayName: string): Promise<void> {
    this.host.logCall(`enhancedLocation.addAsync({ id: "${roomEmail}", type: "room" })`);
    this.host.rooms.update((rooms) => [...rooms.filter((r) => r.email !== roomEmail), { email: roomEmail, name: displayName }]);
    return Promise.resolve();
  }

  override removeRoomLocation(roomEmail: string): Promise<void> {
    this.host.logCall(`enhancedLocation.removeAsync({ id: "${roomEmail}" })`);
    this.host.rooms.update((rooms) => rooms.filter((r) => r.email !== roomEmail));
    return Promise.resolve();
  }

  override on<E extends keyof OfficeAdapterEvents>(event: E, handler: OfficeAdapterEvents[E]): () => void {
    this.handlers[event].add(handler);
    return () => this.handlers[event].delete(handler);
  }

  override officeTheme(): OfficeThemeMode | null {
    return this.host.officeThemeMode();
  }

  override initializationContext(): Promise<PaneInitializationContext | null> {
    const ctx = this.host.initContext();
    this.host.initContext.set(null);
    return Promise.resolve(ctx);
  }

  override openItemForEditing(): Promise<void> {
    this.host.logCall('displayAppointmentForm — reopening the item in compose mode');
    this.host.context.set('compose');
    this.fireTimeChanged();
    return Promise.resolve();
  }

  override showInsightMessage(key: string, message: string): Promise<void> {
    this.host.logCall(`notificationMessages.addAsync("${key}", InsightMessage: "${message}")`);
    return Promise.resolve();
  }

  // ── Harness triggers (called by the simulator compose window) ──────────

  fireTimeChanged(): void {
    this.host.logCall('AppointmentTimeChanged fired');
    const snap = this.snapshot();
    this.handlers.timeChanged.forEach((h) => h(snap));
  }

  fireLocationsChanged(): void {
    this.host.logCall('EnhancedLocationsChanged fired');
    const snap = this.snapshot();
    this.handlers.locationsChanged.forEach((h) => h(snap));
  }

  fireRecipientsChanged(): void {
    const snap = this.snapshot();
    this.handlers.recipientsChanged.forEach((h) => h(snap));
  }

  fireThemeChanged(mode: OfficeThemeMode): void {
    this.host.officeThemeMode.set(mode);
    this.handlers.themeChanged.forEach((h) => h(mode));
  }

  private snapshot(): AppointmentSnapshot {
    return {
      subject: this.host.subject(),
      start: this.host.start(),
      end: this.host.end(),
      roomEmails: this.host.rooms().map((r) => r.email),
      requiredAttendees: this.host.requiredAttendees(),
      optionalAttendees: this.host.optionalAttendees(),
      organizer: this.host.organizer(),
      iCalUId: null,
    };
  }
}
